import React, { useState } from "react";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import studentInLibrary from "../assets/studentsInLibrary.jpg";
import studentInClassroom from "../assets/hero-img-5.jpg";
import ground from "../assets/ground.jpeg";

const PhotoGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const categories = [
    { id: "all", name: "All Photos" },
    { id: "campus", name: "Campus Life" },
    { id: "events", name: "Events" },
    { id: "students", name: "Students" },
    { id: "facilities", name: "Facilities" },
  ];

  const photos = [
    {
      id: 1,
      src: studentInLibrary,
      alt: "Students studying in library",
      category: "campus",
      title: "Library Study Session",
      description:
        "Students collaborating on group projects in our modern library",
    },
    {
      id: 2,
      src: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80",
      src: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80",
      alt: "Computer lab with students",
      category: "facilities",
      title: "Computer Lab",
      description:
        "State-of-the-art computer lab for IT and engineering students",
    },
    {
      id: 3,
      src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80",
      alt: "Graduation ceremony",
      category: "events",
      title: "Graduation Day",
      description: "Celebrating our graduates' achievements and success",
    },
    {
      id: 4,
      src: studentInClassroom,
      alt: "Students in classroom",
      category: "students",
      title: "Interactive Learning",
      description: "Students engaged in hands-on learning activities",
    },
    {
      id: 5,
      src: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80",
      alt: "Engineering workshop",
      category: "facilities",
      title: "Engineering Workshop",
      description:
        "Practical training in our well-equipped engineering workshop",
    },
    {
      id: 6,
      src: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80",
      alt: "Students in lab",
      category: "campus",
      title: "Laboratory Work",
      description:
        "Students conducting experiments in our science laboratories",
    },
    {
      id: 7,
      src: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80",
      alt: "Business presentation",
      category: "events",
      title: "Business Presentation",
      description:
        "Students presenting their business projects to industry experts",
    },
    {
      id: 8,
      src: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
      alt: "Accounting class",
      category: "students",
      title: "Accounting Class",
      description:
        "Students learning financial management and accounting principles",
    },
    {
      id: 9,
      src: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&q=80",
      alt: "Tourism students",
      category: "campus",
      title: "Tourism Studies",
      description: "Students learning about travel and tourism management",
    },
    {
      id: 10,
      src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80",
      alt: "Short course training",
      category: "events",
      title: "Short Course Training",
      description:
        "Professional development through our specialized short courses",
    },
    {
      id: 11,
      src: ground,
      alt: "Campus grounds",
      category: "facilities",
      title: "Campus Grounds",
      description: "Beautiful and well-maintained campus environment",
    },
    {
      id: 12,
      src: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80",
      alt: "Student collaboration",
      category: "students",
      title: "Student Collaboration",
      description: "Students working together on innovative projects",
    },
  ];

  const filteredPhotos =
    selectedCategory === "all"
      ? photos
      : photos.filter((photo) => photo.category === selectedCategory);

  const openLightbox = (photo) => {
    setSelectedPhoto(photo);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
    setIsLightboxOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto?.id);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredPhotos.length - 1;
      setSelectedPhoto(filteredPhotos[prevIndex]);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto?.id);
      const nextIndex = currentIndex < filteredPhotos.length - 1 ? currentIndex + 1 : 0;
      setSelectedPhoto(filteredPhotos[nextIndex]);
    }
  };

  // Add event listener for keyboard navigation
  React.useEffect(() => {
    if (isLightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen, selectedPhoto]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Photo <span className="text-gold">Gallery</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Explore the vibrant life at JRIIT through our collection of
            memorable moments
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? "bg-custom-pink text-white shadow-lg transform scale-105"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-custom-pink"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.id}
                className="break-inside-avoid group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 cursor-pointer"
                onClick={() => openLightbox(photo)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-lg font-bold mb-2">{photo.title}</h3>
                      <p className="text-sm text-white/90">
                        {photo.description}
                      </p>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-gold text-white text-xs font-medium rounded-full">
                      {
                        categories.find((cat) => cat.id === photo.category)
                          ?.name
                      }
                    </span>
                  </div>

                  {/* Zoom Icon */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Photos Message */}
          {filteredPhotos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No photos found
              </h3>
              <p className="text-gray-500">
                Try selecting a different category or check back later for new
                content.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Want to Share Your JRIIT Experience?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Submit your photos and be part of our growing gallery. Share the
            moments that make JRIIT special!
          </p>
          <button className="bg-gold text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-yellow-500 transition-colors duration-300 transform hover:scale-105">
            Submit Photos
          </button>
        </div>
      </section>

      {/* Lightbox Modal */}
      {isLightboxOpen && selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeLightbox}
        >
          <div 
            className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 text-white hover:text-gold transition-colors duration-300 bg-black/50 hover:bg-black/70 rounded-full p-2 sm:p-3 backdrop-blur-sm"
              aria-label="Close lightbox"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Mobile Instructions */}
            <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-10 text-white bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-center sm:hidden">
              Tap outside to close • Swipe to navigate
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredPhotos.length - 1;
                setSelectedPhoto(filteredPhotos[prevIndex]);
              }}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gold transition-colors duration-300 bg-black/50 hover:bg-black/70 rounded-full p-2 sm:p-3 backdrop-blur-sm"
              aria-label="Previous photo"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
                const nextIndex = currentIndex < filteredPhotos.length - 1 ? currentIndex + 1 : 0;
                setSelectedPhoto(filteredPhotos[nextIndex]);
              }}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gold transition-colors duration-300 bg-black/50 hover:bg-black/70 rounded-full p-2 sm:p-3 backdrop-blur-sm"
              aria-label="Next photo"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Main Image */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={selectedPhoto.src}
                alt={selectedPhoto.title}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Image Info */}
            <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 text-white text-center">
              <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3 sm:p-4 max-w-2xl mx-auto">
                <h3 className="text-lg sm:text-xl font-bold mb-2">{selectedPhoto.title}</h3>
                <p className="text-white/90 mb-2 text-sm sm:text-base">{selectedPhoto.description}</p>
                <span className="px-3 py-1 bg-gold text-black text-sm font-medium rounded-full">
                  {categories.find((cat) => cat.id === selectedPhoto.category)?.name}
                </span>
              </div>
            </div>

            {/* Photo Counter */}
            <div className="absolute top-2 sm:top-4 left-2 sm:left-4 z-10 text-white bg-black/50 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
              {filteredPhotos.findIndex(p => p.id === selectedPhoto.id) + 1} of {filteredPhotos.length}
            </div>
          </div>
        </div>
      )}

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default PhotoGallery;
